import { LinkProps, ImageProps, MetaProps } from '@repo/ui/src/types';
import ChevronIcon from '@repo/ui/src/components/Icons/ChevronIcon';
import { StateSetterFunction } from '@repo/ui/src/types/helpers';
import { appConfig } from '../../utils/appConfig';
import LinkGroup, { LinkGroupProps, LinkWithIconProps } from './_LinkGroup';
import LinkList, { LinkListProps } from './_LinkList';
import DynamicComponent from '@repo/ui/src/components/DynamicComponent';
import ProductList from './_ProductList';
import dynamic from 'next/dynamic';
import NavigationPromo from './_NavigationPromo';
import RightContent from './_RightContent';
import { classNames } from '@repo/ui/src/utils/classNames';
const Markdown = dynamic(() => import('@repo/ui/src/components/Markdown'));

export interface NavDropdownProps {
  _meta: MetaProps;
  title?: string;
  description?: string;
  primaryHeader?: string;
  secondaryHeader?: string;
  navigationPromo?: {
    links: Array<LinkProps>;
    title: string;
    image?: ImageProps;
  };
  centerContent?: Array<LinkGroupProps | LinkListProps>;
  rightContent: {
    title: string;
    links: Array<LinkProps>;
    socialLinks?: {
      title?: string;
      links?: Array<LinkWithIconProps>;
    };
    search: {
      searchBy: string;
      searchPlaceholder: string;
    };
  };
  setActiveDropdown?: StateSetterFunction<NavDropdownProps | null>;
  showMobileHeader?: boolean;
}

interface Props extends NavDropdownProps {
  searchLink?: LinkProps;
  SocialLinks?: React.ComponentType<{ socialLinks: any }>;
}
const componentTypes = {
  'linkGroup.json': LinkGroup,
  'linkList.json': LinkList,
  'productList.json': ProductList,
};

const NavDropdown = ({
  SocialLinks,
  centerContent,
  rightContent,
  description,
  title,
  searchLink,
  navigationPromo,
  setActiveDropdown,
  primaryHeader,
  secondaryHeader,
  showMobileHeader = false,
}: Props) => {
  return (
    <>
      {showMobileHeader && (
        <div className="sm:hidden py-2 relative text-black border-b border-b-warm-200 mb-5">
          <button
            className="absolute left-0 p-3 flex"
            aria-label="back previous navigation menu"
            onClick={() => setActiveDropdown?.(null)}
          >
            <ChevronIcon direction="left" className="!w-6 !h-6" />
          </button>
          <h2 className="tra-text-web-heading-4 text-black text-center py-2">{title}</h2>
        </div>
      )}
      <div className="relative gap-y-10 grid content-start sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:grid-cols-[minmax(0,2fr)_minmax(0,4fr)_minmax(0,2fr)] sm:pt-10 md:pb-15 sm:px-6 md:px-10 lg:px-20 w-full h-full gap-7 -z-100 max-w-[1680px] pb-6 sm:max-h-[calc(85vh-var(--tra-nav-height)-var(--dropdown-offset))] sm:overflow-y-auto">
        <div className="hidden lg:flex flex-col max-w-[240px] gap-6">
          <h4
            className={classNames(
              'text-neutral-700',
              appConfig.isMeater && 'tra-text-web-heading-3',
              appConfig.isTraeger && 'tra-text-web-heading-4',
            )}
          >
            {title}
          </h4>
          {description && (
            <Markdown bodySize="large" className="text-neutral-600">
              {description}
            </Markdown>
          )}
        </div>
        <div className="grid gap-y-10">
          {navigationPromo?.title && (
            <NavigationPromo
              {...navigationPromo}
              primaryHeader={primaryHeader}
              secondaryHeader={secondaryHeader}
            />
          )}
          {centerContent?.map((props, i) => (
            <DynamicComponent
              key={i}
              {...props}
              _componentTypes={componentTypes}
              _field="_meta.schema"
              primaryHeader={primaryHeader}
              secondaryHeader={secondaryHeader}
            />
          ))}
        </div>
        <RightContent
          rightContent={rightContent}
          SocialLinks={SocialLinks}
          searchLink={searchLink}
          primaryHeader={primaryHeader}
          secondaryHeader={secondaryHeader}
        />
      </div>
    </>
  );
};

export default NavDropdown;
